// Opt-in canonical-PDS readiness. Upload receipts are NOT a retrieval test. Stock PDSes may
// keep uploads temporary until a record references them; inert staging records anchor batches
// without changing the live site or publishing a half-ready MASL to directory consumers.
import { rawCid } from "@szzt/core";
import type { BuildResult, BuiltBlob } from "@szzt/core";
import { BLOB_STAGING_COLLECTION } from "@szzt/records";
import { uploadBlobs } from "./blobs.ts";
import { atUri, recordCid } from "./records.ts";
import { SwapFailedError } from "./types.ts";
import type { BlobStageCleanup, BlobStageRef, PdsBlobReader, PdsClient } from "./types.ts";

/** Inert internal collection, deliberately NOT a route target or one of COLLECTION's three types. */
export { BLOB_STAGING_COLLECTION } from "@szzt/records";
export const BLOB_STAGE_BATCH_SIZE = 32;

type ReadablePds = PdsClient & PdsBlobReader;

export interface PreparedBlobs {
  stages: BlobStageRef[];
  uploaded: number;
  reused: number;
}

export class BlobReadinessError extends Error {
  constructor(message: string, readonly stages: readonly BlobStageRef[] = [], cause?: unknown) {
    super(`${message}; the live site has not been switched. Staging anchors are retained for same-build resume`,
      cause === undefined ? undefined : { cause });
    this.name = "BlobReadinessError";
  }
}

export function requireBlobReader(client: PdsClient): asserts client is ReadablePds {
  if (typeof client.getBlob !== "function") {
    throw new BlobReadinessError("verifyBlobs requires a canonical PDS getBlob adapter");
  }
}

/** Check the manifest's complete blob set, not merely a caller-supplied subset of built.blobs.
 * The regular builder supplies these invariants; reject mixed/custom BuildResults before writes. */
async function uniqueBlobs(built: BuildResult): Promise<BuiltBlob[]> {
  const blobs = new Map<string, BuiltBlob>();
  for (const blob of built.blobs) {
    if (blob.bytes.length !== blob.size || await rawCid(blob.bytes) !== blob.cid) {
      throw new Error(`invalid local blob bytes or size: ${blob.path}`);
    }
    const prior = blobs.get(blob.cid);
    if (prior !== undefined && (prior.size !== blob.size || prior.blobMimeType !== blob.blobMimeType)) {
      throw new Error(`conflicting blob metadata: ${blob.path}`);
    }
    blobs.set(blob.cid, blob);
  }
  const used = new Set<string>();
  for (const [path, resource] of Object.entries(built.manifest.resources)) {
    const src = resource.src;
    const blob = blobs.get(src.ref.$link);
    if (src.$type !== "blob" || blob === undefined || src.size !== blob.size ||
      src.mimeType !== blob.blobMimeType) throw new Error(`manifest blob not supplied by build: ${path}`);
    used.add(blob.cid);
  }
  if (used.size !== blobs.size) throw new Error("build has unreferenced resource blobs");
  return [...blobs.values()].sort((a, b) => a.cid < b.cid ? -1 : a.cid > b.cid ? 1 : 0);
}

async function usable(client: PdsBlobReader, blob: BuiltBlob): Promise<boolean> {
  const bytes = await client.getBlob(blob.cid);
  return bytes !== null && bytes.length === blob.size && await rawCid(bytes) === blob.cid;
}

/** Recheck ALL referenced bytes immediately before each attempt to switch the site. A transport
 * error is not absence, and a hash mismatch is not success, even with a valid upload receipt. */
export async function verifyBlobReadiness(client: PdsBlobReader, built: BuildResult): Promise<void> {
  const seen = new Set<string>();
  for (const blob of built.blobs) {
    if (seen.has(blob.cid)) continue;
    seen.add(blob.cid);
    if (!await usable(client, blob)) throw new Error(`blob unavailable or hash/size mismatch: ${blob.cid}`);
  }
}

async function anchorBatch(client: ReadablePds, built: BuildResult, batch: BuiltBlob[], index: number): Promise<BlobStageRef> {
  const rkey = `${built.rootCid}-${index}`;
  const value = {
    $type: BLOB_STAGING_COLLECTION,
    root: { $link: built.rootCid },
    blobs: batch.map((b) => ({
      $type: "blob", ref: { $link: b.cid }, mimeType: b.blobMimeType, size: b.size,
    })),
  };
  const cid = await recordCid(value);
  const uri = atUri(client.did, BLOB_STAGING_COLLECTION, rkey);
  for (let attempt = 0; ; attempt++) {
    const existing = await client.getRecord(BLOB_STAGING_COLLECTION, rkey);
    if (existing !== null) {
      // Compare decoded values only for identity. The fetched wire CID is the cleanup CAS token.
      if (existing.uri !== uri || await recordCid(existing.value) !== cid) {
        throw new Error(`staging conflict at ${uri}; refusing to overwrite another value`);
      }
      return { uri, rkey, cid: existing.cid };
    }
    try {
      const written = await client.putRecord({ collection: BLOB_STAGING_COLLECTION, rkey, value, swapRecord: null });
      const stored = await client.getRecord(BLOB_STAGING_COLLECTION, rkey);
      if (written.uri !== uri || stored === null || stored.uri !== uri || stored.cid !== written.cid ||
        await recordCid(stored.value) !== cid) throw new Error(`staging readback differs at ${uri}`);
      return { uri, rkey, cid: stored.cid };
    } catch (err) {
      if (err instanceof SwapFailedError && attempt < 3) continue;
      throw err;
    }
  }
}

/** Probe before upload, verify receipts, anchor bounded batches, then fetch/hash each batch.
 * Deterministic anchors are reused on an exact-build resume and never expire based on a guessed
 * GC timer. Failures retain anchors. No site, deployment, manifest, route or slug is changed here. */
export async function prepareBlobs(client: PdsClient, built: BuildResult): Promise<PreparedBlobs> {
  requireBlobReader(client);
  const result: PreparedBlobs = { stages: [], uploaded: 0, reused: 0 };
  try {
    const blobs = await uniqueBlobs(built);
    for (let offset = 0; offset < blobs.length; offset += BLOB_STAGE_BATCH_SIZE) {
      const batch = blobs.slice(offset, offset + BLOB_STAGE_BATCH_SIZE);
      const missing: BuiltBlob[] = [];
      for (const blob of batch) {
        if (await usable(client, blob)) result.reused++;
        else missing.push(blob);
      }
      await uploadBlobs(client, { ...built, blobs: missing });
      result.uploaded += missing.length;
      result.stages.push(await anchorBatch(client, built, batch, offset / BLOB_STAGE_BATCH_SIZE));
      for (const blob of batch) {
        if (!await usable(client, blob)) throw new Error(`staged blob unavailable or hash/size mismatch: ${blob.cid}`);
      }
    }
    return result;
  } catch (cause) {
    const reason = cause instanceof Error ? cause.message : "blob preparation failed";
    throw new BlobReadinessError(`${reason} (build ${built.rootCid})`, result.stages, cause);
  }
}

/** Only remove exactly the staging versions we used, after permanent manifest refs are installed.
 * A cleanup outage/conflict must be reported as retained anchors, not as a failed site publish. */
export async function cleanupBlobStages(client: PdsClient, stages: readonly BlobStageRef[]): Promise<BlobStageCleanup> {
  const retained: BlobStageRef[] = [];
  let removed = 0;
  for (const stage of stages) {
    for (let attempt = 0; ; attempt++) {
      try {
        const head = await client.getLatestCommit();
        const live = await client.getRecord(BLOB_STAGING_COLLECTION, stage.rkey);
        if (live === null) break;
        if (live.cid !== stage.cid) {
          retained.push(stage);
          break;
        }
        await client.applyWrites({
          swapCommit: head.cid,
          writes: [{ action: "delete", collection: BLOB_STAGING_COLLECTION, rkey: stage.rkey }],
        });
        removed++;
        break;
      } catch (err) {
        if (err instanceof SwapFailedError && attempt < 3) continue;
        retained.push(stage);
        break;
      }
    }
  }
  return { removed, retained };
}
